import React, { useCallback } from 'react';
import { API } from '../api';
import PageHeader from '../ui/PageHeader';
import FilterBar from '../ui/FilterBar';
import useUrlFilters from '../ui/useUrlFilters';
import DataTable, { ColumnsMenu, useColumnVisibility } from '../ui/DataTable';
import ListPage, { useListQuery } from '../ui/ListPage';
import ExportButton from './ExportPanel';
import EntityLink from '../ui/EntityLink';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import { SeverityBadge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import copyText from '../ui/clipboard';

const VULN_FILTER_DEFAULTS = { kw: '', package_name: '', severity: '', fixed: '' };

const VULN_FILTERS = [
  { key: 'kw', type: 'search', label: 'CVE', placeholder: 'CVE-2024-…', mono: true, width: 220 },
  { key: 'package_name', type: 'search', label: 'Package', placeholder: 'Name', mono: true, width: 220 },
  {
    key: 'severity',
    type: 'select',
    label: 'Severity',
    options: [
      { value: '', label: 'All' },
      { value: 'Critical', label: 'Critical' },
      { value: 'High', label: 'High' },
      { value: 'Medium', label: 'Medium' },
      { value: 'Low', label: 'Low' },
    ],
  },
  {
    key: 'fixed',
    type: 'select',
    label: 'Fix',
    options: [
      { value: '', label: 'All' },
      { value: 'available', label: 'Available' },
      { value: 'unavailable', label: 'Not available' },
    ],
  },
];

const affectedPackages = (record) =>
  record.affected_packages
  || (record.package_name ? [{ name: record.package_name, version: record.package_version, fixed_version: record.fixed_version }] : []);

const listOf = (pkgs, line) => pkgs.map(line).join('\n');

function PackagesCell({ record }) {
  const pkgs = affectedPackages(record);
  if (pkgs.length === 0) return <span className="text-graphite">None recorded</span>;
  const names = [...new Set(pkgs.map((p) => p.name))];
  let more = null;
  if (names.length > 1) more = `+${names.length - 1} more`;
  else if (pkgs.length > 1) more = `+${pkgs.length - 1} versions`;
  return (
    <span className="flex items-baseline gap-1.5 min-w-0">
      <EntityLink kind="package" name={names[0]} />
      {more && <span className="shrink-0 text-small text-graphite" title={listOf(pkgs, (p) => `${p.name} ${p.version}`)}>{more}</span>}
    </span>
  );
}

function FixedInCell({ record }) {
  const fixes = affectedPackages(record).filter((p) => p.fixed_version);
  const distinct = [...new Set(fixes.map((p) => p.fixed_version))];
  if (distinct.length === 0) return <span className="text-graphite">No fix yet</span>;
  return (
    <span className="flex items-baseline gap-1.5" title={listOf(fixes, (p) => `${p.name} ${p.version} → ${p.fixed_version}`)}>
      <span className="font-mono text-id text-ok">{distinct[0]}</span>
      {distinct.length > 1 && <span className="text-small text-graphite">+{distinct.length - 1}</span>}
    </span>
  );
}

const descriptionOf = (record) => record.description || record.vuln_description || '';
const publishedOf = (record) => (record.published_at || record.vuln_published_at || '').slice(0, 10);

const VULN_COLUMNS = [
  { key: 'cve', header: 'CVE', hideable: false, render: (r) => <EntityLink kind="cve" cve={r.cve} /> },
  { key: 'severity', header: 'Severity', render: (r) => <SeverityBadge severity={r.severity} /> },
  {
    key: 'description',
    header: 'Description',
    grow: true,
    minWidth: 220,
    fullText: descriptionOf,
    render: (r) => descriptionOf(r) || <span className="text-graphite">No description</span>,
  },
  { key: 'packages', header: 'Packages', render: (r) => <PackagesCell record={r} /> },
  { key: 'hosts', header: 'Hosts', align: 'right', render: (r) => (r.affected_host_count ?? 0).toLocaleString('en-US') },
  { key: 'fixed_version', header: 'Fixed in', render: (r) => <FixedInCell record={r} /> },
  { key: 'published', header: 'Published', render: (r) => <span className="text-graphite tabular-nums">{publishedOf(r) || '—'}</span> },
];

const referencesOf = (record) => (record.reference_url || record.vuln_reference || '').split(',').map((url) => url.trim()).filter(Boolean);

function useVulnRowMenu() {
  const toast = useToast();
  return useCallback((record) => {
    const refs = referencesOf(record);
    const copy = () => copyText(record.cve).then(
      () => toast.success(`Copied ${record.cve}`),
      () => toast.error('Could not copy to the clipboard. Select the CVE ID and copy it manually.'),
    );
    return [
      ...refs.map((url, i) => ({
        key: `ref-${i}`,
        label: refs.length > 1 ? `Open reference ${i + 1}` : (url.includes('nvd.nist.gov') ? 'Open in NVD' : 'Open reference'),
        href: url,
      })),
      { key: 'copy', label: 'Copy CVE ID', onSelect: copy },
    ];
  }, [toast]);
}

const VulnerabilitiesPage = () => {
  const [filters, setFilters] = useUrlFilters(VULN_FILTER_DEFAULTS);
  const columnState = useColumnVisibility(VULN_COLUMNS, 'sbom-columns-vulnerabilities');
  const rowMenu = useVulnRowMenu();

  const key = JSON.stringify([filters.kw, filters.package_name, filters.severity, filters.fixed]);
  const fetchPage = useCallback(
    (paging) => API.GET_VULNS({
      ...paging,
      kw: filters.kw || '',
      package_name: filters.package_name || '',
      severity: filters.severity || undefined,
      fixed: filters.fixed || undefined,
    }),
    [key], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const query = useListQuery(fetchPage, key, 'vulns');

  const hasFilters = Object.keys(VULN_FILTER_DEFAULTS).some((k) => filters[k]);
  const empty = hasFilters ? (
    <EmptyState
      title="No vulnerabilities match these filters"
      action={<Button onClick={() => setFilters(VULN_FILTER_DEFAULTS)}>Clear filters</Button>}
    >
      Check the CVE ID or package name, or widen the severity and fix filters.
    </EmptyState>
  ) : (
    <EmptyState title="No vulnerabilities found">
      Vulnerabilities appear here once hosts report packages with known CVEs.
    </EmptyState>
  );

  return (
    <ListPage>
      <PageHeader
        title="Vulnerabilities"
        actions={(
          <>
            <ColumnsMenu columns={VULN_COLUMNS} hidden={columnState.hidden} onToggle={columnState.toggle} onReset={columnState.reset} />
            <ExportButton />
          </>
        )}
      />
      <FilterBar className="mb-3" filters={VULN_FILTERS} values={filters} onChange={setFilters} />
      <DataTable
        className="flex-1"
        label="Vulnerabilities"
        columns={VULN_COLUMNS}
        hidden={columnState.hidden}
        rows={query.rows}
        rowKey={(r) => r.id ?? r.cve}
        rowLabel={(r) => r.cve}
        rowMenu={rowMenu}
        loading={query.loading}
        error={query.error}
        errorTitle="Vulnerabilities could not be loaded"
        onRetry={query.retry}
        empty={empty}
        pagination={query.pagination}
      />
    </ListPage>
  );
};

export default VulnerabilitiesPage;
